import { createClient } from '@supabase/supabase-js';

// Supabase credentials come from environment (set in /app/frontend/.env)
// REACT_APP_SUPABASE_URL    - the project URL (https://<ref>.supabase.co)
// REACT_APP_SUPABASE_ANON_KEY - the public anon key (safe to ship to browser)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

let supabaseInstance = null;

// Safe storage adapter for Supabase auth. Storage-disabled in-app browsers
// (Instagram/Facebook) THROW on localStorage access, which otherwise produces
// a flood of unhandled promise rejections from the auth client and can break
// getSession/OTP verify. This wrapper tries localStorage and transparently
// falls back to an in-memory map, never throwing. Session persists for the
// page's lifetime even when the device blocks storage.
const createSafeAuthStorage = () => {
  const mem = new Map();
  const ls = () => {
    try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
  };
  return {
    getItem: (key) => {
      try { const v = ls()?.getItem(key); if (v !== null && v !== undefined) return v; } catch { /* blocked */ }
      return mem.has(key) ? mem.get(key) : null;
    },
    setItem: (key, value) => {
      mem.set(key, value);
      try { ls()?.setItem(key, value); } catch { /* blocked - memory only */ }
    },
    removeItem: (key) => {
      mem.delete(key);
      try { ls()?.removeItem(key); } catch { /* blocked */ }
    },
  };
};

export const getSupabase = () => {
  if (supabaseInstance) return supabaseInstance;
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    // Defer the throw until first use so the marketing site stays bootable
    // without Supabase env vars (workspace is a feature, not a hard dep).
    throw new Error(
      'Supabase env vars missing. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in /app/frontend/.env'
    );
  }
  supabaseInstance = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      // Keep a persistent session on the browser - shared with the mobile app
      // identity once the user logs in here.
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: typeof window !== 'undefined' ? createSafeAuthStorage() : undefined,
      storageKey: 'buildpilot.auth',
    },
  });
  return supabaseInstance;
};

export const isSupabaseConfigured = () =>
  Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
